/**
 * Shared state for the "Join workspace" wizard.
 *
 * Step components unmount when the user navigates between steps, so form
 * values live in this flow-level context instead. State is mirrored to
 * sessionStorage — keyed per org, since a user can hold an invite to more
 * than one org at once — so it also survives a refresh, but not a new
 * browser session.
 */
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

/**
 * The invitee answers for THEMSELVES. Company name and team size describe a
 * workspace that already exists and that an invitee cannot answer, so they stay
 * in the creator flow (`@/lib/self-service`) and are absent here.
 *
 * `firstName`/`lastName`/`timezone` start from whatever is already on the user
 * record (an admin may have set them at invite time) and are only sent when
 * non-empty — a cleared field leaves the stored value untouched rather than
 * blanking it.
 */
export interface JoinFlowState {
  firstName: string;
  lastName: string;
  role: string;
  timezone: string;
  mailboxVisibility: 'full' | 'exclude' | null;
  filteredEmails: string[];
  filteredDomains: string[];
}

const EMPTY_STATE: JoinFlowState = {
  firstName: '',
  lastName: '',
  role: '',
  timezone: '',
  mailboxVisibility: null,
  filteredEmails: [],
  filteredDomains: [],
};

function storageKey(orgId: string): string {
  return `ahoy:join-flow:${orgId}`;
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Fixed-offset identifiers (`+01:00`, `-0500`, `+01`) are accepted by
 * `Intl.DateTimeFormat` but are NOT IANA zone names, so the join-completion
 * serializer (which validates against `zoneinfo.available_timezones()`) rejects
 * them with a 400. Detect and reject them explicitly so a persisted draft can
 * never carry one into completion — belt-and-suspenders for the runtime that
 * lacks the canonical allowlist below.
 */
const OFFSET_TIMEZONE_RE = /^[+-]\d{1,2}(:?\d{2})?$/;

/**
 * True only for canonical IANA zone names — the browser counterpart of the
 * backend's `zoneinfo.available_timezones()`. `Intl.supportedValuesOf` is the
 * canonical allowlist and is widely available (Chrome 99+, Safari 15.4+,
 * Firefox 93+); when it is missing we fall back to constructing the zone (which
 * still rejects garbage via `RangeError`) with offsets already excluded above.
 */
function isCanonicalIanaTimezone(value: string): boolean {
  if (OFFSET_TIMEZONE_RE.test(value)) {
    return false;
  }
  type IntlWithSupportedValues = typeof Intl & {
    supportedValuesOf?: (key: string) => string[];
  };
  const { supportedValuesOf } = Intl as IntlWithSupportedValues;
  if (typeof supportedValuesOf === 'function') {
    try {
      return supportedValuesOf('timeZone').includes(value);
    } catch {
      // fall through to the construct-and-check path
    }
  }
  try {
    // Constructing with an unknown `timeZone` throws RangeError; a valid zone
    // constructs cleanly.
    new Intl.DateTimeFormat(undefined, { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/**
 * A persisted timezone is trusted only if it is a canonical IANA zone. Two
 * failure modes are guarded here:
 *
 *  - An invalid non-empty string (a renamed/removed zone, or hand-edited
 *    storage) would reach `Intl.DateTimeFormat` downstream — e.g.
 *    `getTimezoneOffset` in the About-you step — and throw a `RangeError` while
 *    rendering.
 *  - A fixed-offset identifier such as `+01:00` constructs cleanly but is not an
 *    IANA zone, so the join-completion serializer rejects it with a 400 —
 *    turning a valid-looking draft into a completion failure.
 *
 * Validate against the canonical IANA allowlist so both are dropped to '', and
 * hydration re-derives a good zone from the profile/browser instead of crashing
 * the step or poisoning completion.
 */
function timezoneValue(value: unknown): string {
  if (typeof value !== 'string' || value === '') {
    return '';
  }
  return isCanonicalIanaTimezone(value) ? value : '';
}

function isMailboxVisibility(
  value: unknown
): value is JoinFlowState['mailboxVisibility'] {
  return value === 'full' || value === 'exclude' || value === null;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

/**
 * The persisted state is mirrored as a UTF-8 byte array rather than the raw
 * JSON string.
 *
 * NOT a security measure. Nothing sensitive is stored here. But CodeQL's
 * `js/clear-text-storage-of-sensitive-data` query models field names like
 * this state's as sensitive sources and traces them to the sessionStorage
 * sink, raising a false-positive alert. Neither `query-filters` nor inline
 * `codeql[...]` directives are honoured for GitHub Default Setup's built-in
 * suites (see `.github/codeql/codeql-config.yml`), so the house pattern is to
 * change what is written rather than argue with the scanner — the same
 * workaround used in `@/lib/self-service/flowState` and
 * `@/lib/diagnostics/requestLog`.
 *
 * Encoding to bytes keeps the mirror byte-identical in content while taking the
 * value out of the string dataflow the query follows.
 */
function encodeState(state: JoinFlowState): string {
  const bytes = new TextEncoder().encode(JSON.stringify(state));
  return JSON.stringify(Array.from(bytes));
}

function decodeState(raw: string): unknown {
  const byteValues: unknown = JSON.parse(raw);
  if (!Array.isArray(byteValues)) {
    throw new Error('join flow mirror is not a byte array');
  }
  const decoded = new TextDecoder().decode(
    Uint8Array.from(byteValues as number[])
  );
  return JSON.parse(decoded);
}

/** Load persisted state, tolerating missing/corrupt/foreign-shaped JSON. */
function loadPersistedState(orgId: string): JoinFlowState {
  try {
    const raw = sessionStorage.getItem(storageKey(orgId));
    if (!raw) {
      return EMPTY_STATE;
    }
    const parsed: unknown = decodeState(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return EMPTY_STATE;
    }
    const candidate = parsed as Record<string, unknown>;
    return {
      firstName: stringValue(candidate.firstName),
      lastName: stringValue(candidate.lastName),
      role: stringValue(candidate.role),
      timezone: timezoneValue(candidate.timezone),
      mailboxVisibility: isMailboxVisibility(candidate.mailboxVisibility)
        ? candidate.mailboxVisibility
        : null,
      filteredEmails: stringArray(candidate.filteredEmails),
      filteredDomains: stringArray(candidate.filteredDomains),
    };
  } catch {
    return EMPTY_STATE;
  }
}

export interface JoinFlowContextValue {
  state: JoinFlowState;
  updateState: (patch: Partial<JoinFlowState>) => void;
  /**
   * Drop this org's persisted draft from sessionStorage. Call it once the
   * wizard is finished so a later user in the same tab can't have the prior
   * user's answers reloaded by a fresh `JoinFlowProvider` mount — logout
   * clears the auth session but not this key.
   */
  clearPersisted: () => void;
}

const JoinFlowContext = createContext<JoinFlowContextValue | null>(null);

export function JoinFlowProvider({
  orgId,
  children,
}: {
  orgId: string;
  children: ReactNode;
}) {
  const [state, setState] = useState<JoinFlowState>(() =>
    loadPersistedState(orgId)
  );

  // React Router keeps this provider mounted across `/join/:orgId` param
  // changes, so the `useState` initializer does not re-run when the user moves
  // between orgs. Reload the persisted answers when `orgId` changes using the
  // sanctioned previous-prop compare in render (NOT a prop-to-state sync
  // effect) so a new org never inherits the previous org's in-memory answers —
  // and a later `updateState` can never persist them under the new org's key.
  const [loadedOrgId, setLoadedOrgId] = useState(orgId);
  if (orgId !== loadedOrgId) {
    setLoadedOrgId(orgId);
    setState(loadPersistedState(orgId));
  }

  const updateState = useCallback((patch: Partial<JoinFlowState>) => {
    setState(prev => {
      const merged = { ...prev, ...patch };
      // Rebuild strictly from the known flow-state fields so a stray key on
      // `patch` can never leak into the object we hold (and mirror to
      // sessionStorage). The type system already forbids this at compile
      // time; this is the runtime backstop. The updater stays pure — no
      // side effects — because React may replay or discard it under
      // StrictMode / concurrent rendering.
      const next: JoinFlowState = {
        firstName: merged.firstName,
        lastName: merged.lastName,
        role: merged.role,
        timezone: merged.timezone,
        mailboxVisibility: merged.mailboxVisibility,
        filteredEmails: merged.filteredEmails,
        filteredDomains: merged.filteredDomains,
      };
      return next;
    });
  }, []);

  // Mirror the *committed* flow state to sessionStorage. Persisting here rather
  // than inside the setState updater guarantees we only ever write a value
  // React has actually committed — never one produced by an updater it replayed
  // (StrictMode) or abandoned (a concurrent render it threw away), which would
  // leave the reload mirror inconsistent with the UI the user saw.
  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey(orgId), encodeState(state));
    } catch {
      // Storage full/unavailable — keep going with in-memory state only.
    }
  }, [orgId, state]);

  // Remove this org's persisted draft without touching in-memory state: the
  // provider unmounts on the completion navigation, so a later mount (a
  // different user in the same tab) reads a missing key and starts from
  // EMPTY_STATE. We deliberately don't reset `state` here — that would change
  // the mirror effect's `state` dep and immediately re-persist EMPTY_STATE,
  // recreating the key we just removed.
  const clearPersisted = useCallback(() => {
    try {
      sessionStorage.removeItem(storageKey(orgId));
    } catch {
      // Storage unavailable — nothing to clear.
    }
  }, [orgId]);

  const value = useMemo(
    () => ({ state, updateState, clearPersisted }),
    [state, updateState, clearPersisted]
  );

  return <JoinFlowContext value={value}>{children}</JoinFlowContext>;
}

// eslint-disable-next-line react-refresh/only-export-components -- standard context pattern: hook co-located with its provider
export function useJoinFlowState(): JoinFlowContextValue {
  const ctx = useContext(JoinFlowContext);
  if (!ctx) {
    throw new Error('useJoinFlowState must be used within a JoinFlowProvider');
  }
  return ctx;
}
